// Ambient life: flocks of birds circling over the land and low-poly clouds
// drifting around the horizon. Purely cosmetic and deterministic in layout
// (seeded by index), animated per frame on one InstancedMesh each.
//
// Clouds stay in a ring outside the playable map, so they never sit between
// the camera and the board; they carry no fog (the haze would erase them)
// but are tinted toward the horizon colour.

import { useFrame } from '@react-three/fiber';
import { useMemo, useRef } from 'react';
import * as THREE from 'three';
import { merge } from './TerrainScatterMaterials.js';
import { hexNoise } from './elevation.js';

const _obj = new THREE.Object3D();

// --- Birds ---

/** One bird: two wing triangles joined at the body (a flat, shallow "V"). */
const birdGeo = (() => {
  const wing = (side: 1 | -1) => {
    const g = new THREE.BufferGeometry();
    g.setAttribute(
      'position',
      new THREE.BufferAttribute(
        new Float32Array([0, 0, 0.03, 0, 0, -0.03, side * 0.13, 0, -0.01]),
        3,
      ),
    );
    g.setAttribute('color', new THREE.BufferAttribute(new Float32Array(9).fill(1), 3));
    return g;
  };
  return merge([wing(1), wing(-1)]);
})();
const birdMat = new THREE.MeshBasicMaterial({ color: '#2b2f36', side: THREE.DoubleSide });

export interface BirdPose {
  readonly x: number;
  readonly y: number;
  readonly z: number;
  /** Heading (radians about Y) — tangent to the circling path. */
  readonly heading: number;
  /** Wing flap in [-1, 1]. */
  readonly flap: number;
}

const FLOCKS = 2;
const BIRDS_PER_FLOCK = 7;

/**
 * Pose of bird `i` at time `t` (seconds), circling `center`. Pure and
 * deterministic: each flock has its own radius, height, speed and offset;
 * birds trail their leader in a loose formation.
 */
export function birdPose(
  i: number,
  t: number,
  center: readonly [number, number, number],
): BirdPose {
  const flock = Math.floor(i / BIRDS_PER_FLOCK);
  const slot = i % BIRDS_PER_FLOCK;
  const radius = 5 + flock * 4 + hexNoise(flock, 1) * 2;
  const speed = (0.12 + hexNoise(flock, 2) * 0.05) * (flock % 2 === 0 ? 1 : -1);
  const angle = t * speed + flock * 2.4 - slot * 0.045 * Math.sign(speed);
  const side = (slot % 2 === 0 ? 1 : -1) * Math.ceil(slot / 2) * 0.12;
  const r = radius + side;
  return {
    x: center[0] + Math.cos(angle) * r,
    y: 2.6 + flock * 0.7 + Math.sin(t * 0.7 + i) * 0.08,
    z: center[2] + Math.sin(angle) * r,
    heading: -angle - (Math.sign(speed) * Math.PI) / 2,
    flap: Math.sin(t * 9 + i * 1.7),
  };
}

export function Birds({ center }: { center: readonly [number, number, number] }) {
  const ref = useRef<THREE.InstancedMesh>(null);
  const count = FLOCKS * BIRDS_PER_FLOCK;
  useFrame(({ clock }) => {
    const mesh = ref.current;
    if (!mesh) return;
    const t = clock.getElapsedTime();
    for (let i = 0; i < count; i++) {
      const p = birdPose(i, t, center);
      _obj.position.set(p.x, p.y, p.z);
      _obj.rotation.set(0, p.heading, 0);
      // Flap: the wing tips are raised (dihedral, below), so scaling Y
      // lifts and drops them; a small body bob sells the stroke.
      _obj.scale.set(1, 1 + p.flap * 0.9, 1);
      _obj.position.y += p.flap * 0.015;
      _obj.updateMatrix();
      mesh.setMatrixAt(i, _obj.matrix);
    }
    mesh.instanceMatrix.needsUpdate = true;
  });
  // Wings get their "flap" from a slight dihedral: tilt the wing tips up so
  // the Y-scale above visibly raises and lowers them.
  const geo = useMemo(() => {
    const g = birdGeo.clone();
    const pos = g.getAttribute('position') as THREE.BufferAttribute;
    for (let v = 0; v < pos.count; v++) pos.setY(v, Math.abs(pos.getX(v)) * 0.35);
    pos.needsUpdate = true;
    return g;
  }, []);
  return <instancedMesh ref={ref} args={[geo, birdMat, count]} frustumCulled={false} />;
}

// --- Distant clouds ---

const CLOUDS = 16;
export const cloudGeo = (() => {
  const puff = (x: number, y: number, z: number, r: number) =>
    (() => {
      const g = new THREE.IcosahedronGeometry(r, 1).translate(x, y, z).toNonIndexed();
      g.setAttribute(
        'color',
        new THREE.BufferAttribute(
          new Float32Array(g.getAttribute('position').count * 3).fill(1),
          3,
        ),
      );
      return g;
    })();
  return merge([
    puff(0, 0, 0, 1.6),
    puff(1.7, -0.3, 0.2, 1.2),
    puff(-1.6, -0.35, -0.1, 1.15),
    puff(0.6, 0.7, -0.3, 1.1),
    puff(-0.5, 0.5, 0.6, 0.95),
  ]);
})();
const cloudMat = new THREE.MeshBasicMaterial({
  color: '#e4ebf1',
  transparent: true,
  opacity: 0.9,
  fog: false,
  depthWrite: false,
});

/** Cloud `i`'s ring placement (radius, height, angular offset, size) around the map. */
export function cloudSlot(i: number): { radius: number; y: number; angle: number; scale: number } {
  return {
    radius: 55 + hexNoise(i, 11) * 55,
    y: 5.5 + hexNoise(i, 12) * 5,
    angle: (i / CLOUDS) * Math.PI * 2 + hexNoise(i, 13) * 0.35,
    scale: 1.4 + hexNoise(i, 14) * 1.8,
  };
}

export function DistantClouds({ center }: { center: readonly [number, number, number] }) {
  const ref = useRef<THREE.InstancedMesh>(null);
  useFrame(({ clock }) => {
    const mesh = ref.current;
    if (!mesh) return;
    const drift = clock.getElapsedTime() * 0.006; // one lap ≈ 17 minutes
    for (let i = 0; i < CLOUDS; i++) {
      const c = cloudSlot(i);
      const a = c.angle + drift;
      _obj.position.set(
        center[0] + Math.cos(a) * c.radius,
        c.y,
        center[2] + Math.sin(a) * c.radius,
      );
      _obj.rotation.set(0, -a, 0);
      _obj.scale.set(c.scale * 1.6, c.scale * 0.55, c.scale);
      _obj.updateMatrix();
      mesh.setMatrixAt(i, _obj.matrix);
    }
    mesh.instanceMatrix.needsUpdate = true;
  });
  return <instancedMesh ref={ref} args={[cloudGeo, cloudMat, CLOUDS]} frustumCulled={false} />;
}
